import { Component, h, Host, Prop, forceUpdate, Watch } from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";
import loadTranslations from "../../utils/loadTranslations";
import { createAuthStore, SilentAuth } from "../../utils/auth";
import { EnvConfig, getEnvConfig, setEnv } from "../../api/config";
import { Documents } from "../../api/publicApi/serviceReports/documents";
import { GroupedReports } from "./grouped-reports";
import { Filter } from "./filter";

@Component({
    tag: "mf-service-reports",
    styleUrl: "service-reports.scss",
    shadow: false
})
export class ServiceReports {
    /** identifier of Machine - one of machineNumber OR missionNumber are required */
    @Prop() machineNumber: string;
    /** identifier of Mission - one of machineNumber OR missionNumber are required */
    @Prop() missionNumber: string;
    /**
     * The locale to use for translations and the phone input
     */
    @Prop({ reflect: true }) locale: string = "en-US";
    /** environment */
    @Prop() env: string;
    /**
     * use onprem Public API instead of azure
     * @deprecated only used until April 2025
     */
    @Prop() legacyApi: boolean = false;
    /**
     * technician name to be used in the reports
     */
    @Prop() technicianName: string;
    /**
     * machine alias to be used in the reports
     */
    @Prop() machineAlias: string;
    /**
     * machine name to be used in the reports
     */
    @Prop() machineName: string;
    /**
     * don't show the filter.
     * Regardless the filter will always automatically be hidden when filtering does not make sense (e.g. only one document).
     */
    @Prop() hideFilter: boolean = false;

    private config: EnvConfig;
    private authStore = createAuthStore();
    private documents: Documents;
    private i18nSubscriberId: string;

    componentWillLoad() {
        setEnv(`${this.env}${this.legacyApi ? "Legacy" : ""}`);
        this.config = getEnvConfig();
        this.documents = new Documents(this.config, () => this.onTokenReady());
    }

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
        this.loadTranslations();
    }

    @Watch("locale")
    onLocaleChange() {
        if (this.locale !== i18nProvider.language) {
            this.loadTranslations();
        }
    }

    private loadTranslations() {
        loadTranslations(this.locale).then((locale) => {
            i18nProvider.changeLanguage(locale);
            this.locale = locale;
        });
    }

    disconnectedCallback() {
        const { documents } = this.documents.store.state;
        documents?.forEach((doc) => doc.destroy());
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    private async onTokenReady() {
        if (this.missionNumber) {
            await this.documents.getDocumentsForMission(this.missionNumber);
        } else if (this.machineNumber) {
            await this.documents.getDocumentsForMachine(this.machineNumber);
        }
    }

    private handleFilterChange = (filters: {
        assignmentTypes: string[] | null;
        startDate: string | null;
        endDate: string | null;
    }) => {
        this.documents.store.state.filters = filters;
    };

    render() {
        const { isLoading, error, documents, maxDate, minDate } = this.documents.store.state;
        const { t } = i18nProvider;
        const isOnMachinePage = !!this.machineNumber && !this.missionNumber;

        return (
            <Host class="mf-service-reports">
                <h2 class="ux-text-headline ux-text-headline--big ux-u-text-align--center">
                    {t("mf-service-calls:serviceReports.headline")}
                </h2>
                {isOnMachinePage && <p class="ux-text-normal">{t("mf-service-calls:serviceReports.sublineMachine")}</p>}
                {!this.hideFilter && (
                    <Filter
                        handleFilterChange={this.handleFilterChange}
                        t={t}
                        minDate={minDate}
                        maxDate={maxDate}
                        documents={documents}
                    />
                )}
                <GroupedReports
                    t={t}
                    documents={documents}
                    filteredDocuments={this.documents.getFilteredDocuments(documents)}
                    isOnMachinePage={isOnMachinePage}
                    machineNumber={this.machineNumber}
                    machineAlias={this.machineAlias}
                    machineName={this.machineName}
                    technicianName={this.technicianName}
                />
                {isLoading && <ux-loader variant="spinner" />}
                {error && (
                    <div class="mf-service-reports__error ux-text-normal">
                        {t("mf-service-calls:serviceReports.loadError")}: {error}
                    </div>
                )}
                <SilentAuth disabled={false} config={this.config} authStore={this.authStore} />
            </Host>
        );
    }
}
